/**
 * useSiteConfig
 * --------------------------------------------------------------
 * Shared tenant-aware site config hook for the public site.
 * - Resolves tenant from URL :municipio param (public) or from
 *   localStorage admin_selected_municipio (admin routes).
 * - Retries on transient network errors (initial-navigation aborts).
 * - Listens to "siteConfig:updated" so changes from Admin >
 *   Configurações do Site reflect everywhere without reload.
 *
 * Returns: { config, loading, refresh }
 *   config = { logo_principal, logo_footer, logo_admin, favicon, ... }
 *            (empty object until first successful fetch)
 */
import { useEffect, useState, useCallback } from 'react';
import { useLocation, useParams } from 'react-router-dom';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const FETCH_TIMEOUT_MS = 8000;
const MAX_RETRIES = 2;

function buildUrl(pathname, paramsObj) {
  const isAdmin = pathname.startsWith('/admin');
  const endpoint = isAdmin ? '/admin/configuracoes' : '/configuracoes';
  const qs = new URLSearchParams();
  Object.entries(paramsObj).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.append(k, v);
  });
  return `${BACKEND}/api${endpoint}${qs.toString() ? `?${qs}` : ''}`;
}

async function fetchWithRetry(url, attempt = 0) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    // Alinha com getAuthToken() do api.js — ordem: token, adminToken,
    // admin_token, authToken, access_token. Sem isso, o hook pegava só
    // access_token/token e mandava request SEM Authorization em quem usa
    // adminToken → 401.
    const token =
      localStorage.getItem('token') ||
      localStorage.getItem('adminToken') ||
      localStorage.getItem('admin_token') ||
      localStorage.getItem('authToken') ||
      localStorage.getItem('access_token');
    const res = await fetch(url, {
      signal: controller.signal,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    clearTimeout(timer);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (err) {
    clearTimeout(timer);
    if (attempt < MAX_RETRIES) {
      await new Promise(r => setTimeout(r, 600 * (attempt + 1)));
      return fetchWithRetry(url, attempt + 1);
    }
    throw err;
  }
}

export default function useSiteConfig() {
  const location = useLocation();
  const { municipioSlug, municipio } = useParams();
  const slug = municipioSlug || municipio;
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const params = {};
      if (location.pathname.startsWith('/admin')) {
        try {
          const saved = localStorage.getItem('admin_selected_municipio');
          if (saved && saved !== 'null' && saved !== 'global') {
            const parsed = JSON.parse(saved);
            if (parsed?.id) params.municipio_id = parsed.id;
          }
        } catch { /* ignore */ }
      } else if (slug) {
        params.municipio_slug = slug;
      }
      const url = buildUrl(location.pathname, params);
      const data = await fetchWithRetry(url);
      setConfig(data || {});
    } catch (e) {
      // Keep previous config on error so we don't flash defaults
      // eslint-disable-next-line no-console
      console.warn('[useSiteConfig] failed to load:', e?.message || e);
    } finally {
      setLoading(false);
    }
  }, [location.pathname, slug]);

  useEffect(() => {
    let alive = true;
    // Small delay so the initial page navigation doesn't abort us.
    const t = setTimeout(() => { if (alive) refresh(); }, 200);
    const onUpdated = () => refresh();
    window.addEventListener('siteConfig:updated', onUpdated);
    return () => {
      alive = false;
      clearTimeout(t);
      window.removeEventListener('siteConfig:updated', onUpdated);
    };
  }, [refresh]);

  return { config, loading, refresh };
}
